import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import PortalLayout from '../components/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Send, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TalentMessaging = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const candidateId = searchParams.get('candidateId');
  
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchThreads();
  }, []);

  useEffect(() => {
    if (candidateId && threads.length > 0) {
      const thread = threads.find(t => t.candidate_user_id === candidateId);
      if (thread) {
        selectThread(thread);
      }
    }
  }, [candidateId, threads]);

  const fetchThreads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/portal/talent/messages`);
      setThreads(response.data.threads || []);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const selectThread = async (thread) => {
    setSelectedThread(thread);
    
    try {
      const response = await axios.get(`${API}/portal/talent/messages/${thread.id}`);
      setMessages(response.data.messages || []);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !selectedThread) return;
    
    setSending(true);
    
    try {
      await axios.post(`${API}/portal/talent/messages/${selectedThread.id}`, {
        body: newMessage
      });
      
      setNewMessage('');
      
      // Refresh messages
      const response = await axios.get(`${API}/portal/talent/messages/${selectedThread.id}`);
      setMessages(response.data.messages || []);
      
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <PortalLayout>
      <div className="h-[calc(100vh-8rem)]">
        <Card className="h-full flex flex-col">
          <CardHeader>
            <CardTitle>Messages</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex gap-4 p-4 overflow-hidden">
            {/* Thread List */}
            <div className="w-80 border-r border-border overflow-y-auto">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : threads.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No conversations yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {threads.map((thread) => (
                    <button
                      key={thread.id}
                      onClick={() => selectThread(thread)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        selectedThread?.id === thread.id
                          ? 'bg-primary/10 border border-primary'
                          : 'hover:bg-muted border border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4" />
                        <span className="font-semibold text-sm">{thread.candidate_name || 'Candidate'}</span>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {thread.last_message_preview || 'No messages'}
                      </p>
                      {thread.unread_count_company > 0 && (
                        <Badge className="mt-1 text-xs">{thread.unread_count_company} new</Badge>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Message Area */}
            <div className="flex-1 flex flex-col">
              {selectedThread ? (
                <>
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto mb-4 space-y-4">
                    {messages.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No messages yet. Start the conversation!
                      </div>
                    ) : (
                      messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_type === 'COMPANY' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-md px-4 py-3 rounded-lg ${
                              msg.sender_type === 'COMPANY'
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-line">{msg.body}</p>
                            <p className="text-xs opacity-70 mt-1">
                              {new Date(msg.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Send Message */}
                  <form onSubmit={sendMessage} className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      disabled={sending}
                    />
                    <button
                      type="submit"
                      disabled={sending || !newMessage.trim()}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Select a conversation to view messages</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
};

export default TalentMessaging;
